import React from "react";
import { FormattedMessage } from "react-intl";
import { DECORATORS, SIZE, TYPE } from "components/Form";
import { ICON_TYPE, ICON_SIZE, ICON_POSITION, MessageWithIcon } from "components/Icon";
import { GET_TIME_ZONES, GET_ADMINS, GET_DISCIPLINES, GET_SKILLS, GET_LOCATIONS, GET_MISSIONS } from "graphql/queries";
import {
  ARCHETYPES_OPTIONS,
  WORK_ARRANGEMENT_OPTIONS,
  ENGAGEMENT_TYPES_OPTIONS,
  RATE_FREQUENCIES,
  CLIENT_BUDGET_RATE_FREQUENCIES,
  MINIMUM_TARGET_RATE,
  CURRENCY_NAMES,
  CURRENCIES,
} from "constants/index";
import { getPlainText } from "components/Form";
import { formatNumber, isEmpty, getApiLimitSuffix } from "utils";

const MISSION_ROLE_FORM_FIELDS = {
  name: {
    label: "Role Name",
    type: TYPE.input,
    size: SIZE.xlarge,
    properties: {
      name: "name",
      type: "text",
      maxLength: 100,
      required: true,
      placeholder: "Name the role",
    },
  },
  archetype: ({ archetypeOptions = ARCHETYPES_OPTIONS }) => ({
    label: "Team Role Archetype",
    type: TYPE.tags,
    size: SIZE.xlarge,
    properties: {
      name: "archetype",
      placeholder: "Select role archetype",
      required: true,
      minLength: 0,
      options: archetypeOptions,
      labelKey: "name",
      valueKey: "id",
    },
  }),
  description: {
    label: "Role Description",
    type: TYPE.editor,
    size: SIZE.xlarge,
    onChange: (name, value, handleOnChange) => {
      handleOnChange("description", getPlainText(value));
      handleOnChange(name, value);
    },
    properties: {
      name: "descriptionJson",
      maxLength: 3000,
      required: true,
      placeholder: "Describe this role for prospective squad members",
      showFormattingOptions: true,
    },
  },
  primarySkills: ({ onSkillsChange, skills, skillsMatchCount, skillsLoading, domainId }) => ({
    label: "Required Skills",
    type: TYPE.tags,
    size: SIZE.xlarge,
    onChange: onSkillsChange,
    note: skills?.length > 0 && (skillsLoading || !isEmpty(skillsMatchCount)) && (
      <MessageWithIcon
        iconType={ICON_TYPE.checkmark}
        isSuccess={skillsMatchCount > 0}
        loading={skillsLoading}
        messageId="Mission.RoleForm.RequiredSkills.MatchCount"
        messageValues={{
          name: (
            <b>
              <FormattedMessage
                id="Mission.RoleForm.RequiredSkills.MatchCountName"
                values={{
                  count: skillsMatchCount,
                  formattedNumber: formatNumber(skillsMatchCount, getApiLimitSuffix(skillsMatchCount)),
                }}
              />
            </b>
          ),
        }}
      />
    ),
    properties: {
      name: "primarySkills",
      placeholder: "Type a skill...",
      queryName: GET_SKILLS,
      queryKeyName: "skills",
      queryVariables: { domainId },
      required: true,
      minLength: 1,
    },
  }),
  secondarySkills: ({ domainId }) => ({
    label: "Nice-to-have Skills",
    type: TYPE.tags,
    size: SIZE.xlarge,
    properties: {
      name: "secondarySkills",
      placeholder: "Type a skill...",
      queryName: GET_SKILLS,
      queryKeyName: "skills",
      queryVariables: { domainId },
      required: false,
      minLength: 1,
    },
  }),
  disciplines: ({ domainId }) => ({
    label: "Required Disciplines",
    type: TYPE.tags,
    size: SIZE.xlarge,
    properties: {
      name: "disciplines",
      placeholder: "Type a discipline...",
      queryName: GET_DISCIPLINES,
      queryKeyName: "disciplines",
      queryVariables: { domainId },
      minLength: 0,
      required: true,
    },
  }),
  remoteAnywhere: {
    label: "Region & Country Restrictions",
    type: TYPE.checkbox,
    size: SIZE.xlarge,
    properties: {
      type: "checkbox",
      name: "remoteAnywhere",
      label: "Anywhere",
    },
  },
  locations: {
    type: TYPE.tags,
    size: SIZE.xlarge,
    disabledCondition: {
      field: "remoteAnywhere",
      value: true,
    },
    properties: {
      name: "locations",
      placeholder: "Type a location...",
      queryName: GET_LOCATIONS,
      queryKeyName: "locations",
      searchQuery: (search) => ({ filters: { search } }),
      minLength: 0,
      autoFocus: false,
      defaultOpen: false,
    },
  },
  startDate: {
    label: "Start Date",
    type: TYPE.datePicker,
    size: SIZE.xlarge,
    properties: {
      name: "startDate",
      placeholder: "Estimated start date",
      required: true,
    },
  },
  engagementType: {
    label: "Engagement Type",
    type: TYPE.select,
    size: SIZE.xlarge,
    properties: {
      name: "engagementType",
      required: true,
      options: ENGAGEMENT_TYPES_OPTIONS,
    },
  },
  engagementTime: {
    label: "Commitment (hours / week)",
    type: TYPE.input,
    size: SIZE.xlarge,
    valueFunc: (val) => parseInt(val),
    properties: {
      name: "engagementTime",
      placeholder: "Expected weekly hours",
      required: true,
      type: "number",
    },
    decorator: {
      type: DECORATORS.icon,
      props: {
        type: ICON_TYPE.hourglass,
        size: ICON_SIZE.medium,
        position: ICON_POSITION.overlay,
      },
    },
  },
  location: {
    type: TYPE.geoLocator,
    size: SIZE.xlarge,
    properties: {
      name: "location",
      type: "text",
      label: "On-Site Location",
      localityOnly: true,
      isClearable: true,
      required: false,
    },
  },
  workArrangement: {
    label: "Work Arrangement",
    type: TYPE.select,
    size: SIZE.xlarge,
    properties: {
      name: "workArrangement",
      required: true,
      options: WORK_ARRANGEMENT_OPTIONS,
    },
    decorator: {
      type: DECORATORS.icon,
      props: {
        type: ICON_TYPE.clock,
        size: ICON_SIZE.medium,
        position: ICON_POSITION.overlay,
      },
    },
  },
  targetRate: ({ min }) => ({
    label: "Target Rate",
    type: TYPE.rateInput,
    size: SIZE.xlarge,
    valueFunc: ({ currency, amount }) => ({ amount: parseFloat(amount), currency }),
    properties: {
      name: "targetRate",
      type: "number",
      required: true,
      disableFrequency: true,
      rateKeyName: "amount",
      min,
    },
  }),
  timezone: {
    label: "Preferred Time Zone",
    type: TYPE.select,
    size: SIZE.xlarge,
    properties: {
      name: "timezone",
      queryName: GET_TIME_ZONES,
      queryKeyName: "timezones",
      labelKey: "name",
      required: true,
    },
    decorator: {
      type: DECORATORS.icon,
      props: {
        type: ICON_TYPE.pin,
        size: ICON_SIZE.medium,
        position: ICON_POSITION.overlay,
      },
    },
  },
  assignedToId: {
    label: "Sales Owner",
    type: TYPE.select,
    size: SIZE.xlarge,
    properties: {
      name: "assignedToId",
      queryName: GET_ADMINS,
      queryKeyName: "admins",
      labelKey: "name",
      valueKey: "id",
      isSearchable: true,
      querySearchFieldName: "search",
      required: true,
      noOptionsMessage: "No admins found",
    },
    decorator: {
      type: DECORATORS.icon,
      props: {
        type: ICON_TYPE.user,
        size: ICON_SIZE.medium,
        position: ICON_POSITION.overlay,
      },
    },
  },
  talentOwnerId: {
    label: "Talent Owner",
    type: TYPE.select,
    size: SIZE.xlarge,
    properties: {
      name: "talentOwnerId",
      queryName: GET_ADMINS,
      queryKeyName: "admins",
      labelKey: "name",
      valueKey: "id",
      isSearchable: true,
      querySearchFieldName: "search",
      noOptionsMessage: "No admins found",
    },
    decorator: {
      type: DECORATORS.icon,
      props: {
        type: ICON_TYPE.user,
        size: ICON_SIZE.medium,
        position: ICON_POSITION.overlay,
      },
    },
  },
  isVisible: {
    label: "Status",
    type: TYPE.checkbox,
    size: SIZE.xlarge,
    properties: {
      name: "isVisible",
      label: "This role is open for applications",
    },
    disabledCondition: {
      field: "state",
      value: "CLOSED",
    },
  },
  state: {
    type: TYPE.hidden,
    properties: {
      name: "state",
    },
  },
  mission: ({ setMissionId = () => {} } = {}) => ({
    label: "Mission / Project",
    type: TYPE.select,
    size: SIZE.xlarge,
    onChange: (name, value, handleOnChange) => {
      setMissionId(value);
      handleOnChange(name, value);
    },
    properties: {
      name: "missionId",
      placeholder: "Type a mission name...",
      queryName: GET_MISSIONS,
      queryKeyName: "missions",
      queryVariables: { filters: { published: true, includeDefault: true } },
      labelKey: "name",
      valueKey: "id",
      searchQuery: (search) => ({ filters: { search, published: true, includeDefault: true } }),
      minLength: 1,
    },
  }),
  customerBudget: {
    label: "Budget (compensation or pay rate)",
    type: TYPE.minMaxInput,
    parentObjectKey: "customerBudget",
    valueFunc: ({ min, max }) => ({ min: parseFloat(min), max: parseFloat(max) }),
    properties: {
      name: "rate",
      type: "number",
      required: false,
    },
  },
  customerBudgetCurrency: {
    type: TYPE.select,
    size: SIZE.medium,
    parentObjectKey: "customerBudget",
    properties: {
      name: "currency",
      required: false,
      options: CURRENCIES,
      defaultValue: CURRENCY_NAMES.cad,
    },
  },
  customerBudgetFrequency: {
    type: TYPE.select,
    size: SIZE.medium,
    parentObjectKey: "customerBudget",
    properties: {
      name: "frequency",
      required: false,
      options: CLIENT_BUDGET_RATE_FREQUENCIES,
      defaultValue: RATE_FREQUENCIES.hourly,
    },
  },
  attachments: {
    label: "Attachments",
    description: <FormattedMessage id="RoleAttachments.AddAttachmentForm.Description" />,
    type: TYPE.fileDropzone,
    size: SIZE.xlarge,
    properties: {
      name: "attachments",
      accept: "application/pdf",
      maxFiles: 5,
      required: false,
      dropzoneTextId: "Mission.RoleForm.Attachments.DropzoneText",
    },
  },
};

const getLeftColFields = ({ isClient, isTalentSimpleClient, isRoleBuilder, ...rest }) => {
  switch (true) {
    case isRoleBuilder:
      return [
        MISSION_ROLE_FORM_FIELDS.archetype(rest),
        MISSION_ROLE_FORM_FIELDS.disciplines(rest),
        MISSION_ROLE_FORM_FIELDS.primarySkills(rest),
        MISSION_ROLE_FORM_FIELDS.secondarySkills(rest),
        MISSION_ROLE_FORM_FIELDS.remoteAnywhere,
        MISSION_ROLE_FORM_FIELDS.locations,
      ];
    default:
      return [
        MISSION_ROLE_FORM_FIELDS.name,
        MISSION_ROLE_FORM_FIELDS.archetype(rest),
        MISSION_ROLE_FORM_FIELDS.description,
        MISSION_ROLE_FORM_FIELDS.disciplines(rest),
        MISSION_ROLE_FORM_FIELDS.primarySkills(rest),
        MISSION_ROLE_FORM_FIELDS.secondarySkills(rest),
        MISSION_ROLE_FORM_FIELDS.remoteAnywhere,
        MISSION_ROLE_FORM_FIELDS.locations,
        ...(isClient && !isTalentSimpleClient ? [MISSION_ROLE_FORM_FIELDS.attachments] : []),
      ];
  }
};

const getRightColFields = ({ isAdmin, isClient, isTalentSimpleClient, isRoleBuilder, ...rest }) => {
  switch (true) {
    case isRoleBuilder:
      return [
        { ...MISSION_ROLE_FORM_FIELDS.startDate, size: SIZE.medium },
        { ...MISSION_ROLE_FORM_FIELDS.engagementType, size: SIZE.medium },
        { ...MISSION_ROLE_FORM_FIELDS.location, size: SIZE.medium },
        { ...MISSION_ROLE_FORM_FIELDS.engagementTime, size: SIZE.medium },
        {
          ...MISSION_ROLE_FORM_FIELDS.customerBudget,
          size: SIZE.medium,
          properties: { ...MISSION_ROLE_FORM_FIELDS.customerBudget.properties, required: true },
        },
        {
          ...MISSION_ROLE_FORM_FIELDS.customerBudgetCurrency,
          label: "Currency",
          size: SIZE.small,
          properties: { ...MISSION_ROLE_FORM_FIELDS.customerBudgetCurrency.properties, required: true },
        },
        {
          ...MISSION_ROLE_FORM_FIELDS.customerBudgetFrequency,
          label: "Frequency",
          size: SIZE.small,
          properties: { ...MISSION_ROLE_FORM_FIELDS.customerBudgetFrequency.properties, required: true },
        },
        { ...MISSION_ROLE_FORM_FIELDS.timezone, size: SIZE.medium },
      ];
    case isAdmin:
      return [
        MISSION_ROLE_FORM_FIELDS.startDate,
        MISSION_ROLE_FORM_FIELDS.engagementType,
        MISSION_ROLE_FORM_FIELDS.workArrangement,
        MISSION_ROLE_FORM_FIELDS.location,
        MISSION_ROLE_FORM_FIELDS.engagementTime,
        MISSION_ROLE_FORM_FIELDS.customerBudget,
        MISSION_ROLE_FORM_FIELDS.customerBudgetCurrency,
        MISSION_ROLE_FORM_FIELDS.customerBudgetFrequency,
        MISSION_ROLE_FORM_FIELDS.targetRate({ min: MINIMUM_TARGET_RATE }),
        MISSION_ROLE_FORM_FIELDS.timezone,
        MISSION_ROLE_FORM_FIELDS.assignedToId,
        MISSION_ROLE_FORM_FIELDS.talentOwnerId,
        MISSION_ROLE_FORM_FIELDS.state,
      ];
    case isClient:
      return [
        ...(isTalentSimpleClient ? [] : [MISSION_ROLE_FORM_FIELDS.mission(rest)]),
        MISSION_ROLE_FORM_FIELDS.startDate,
        MISSION_ROLE_FORM_FIELDS.engagementType,
        MISSION_ROLE_FORM_FIELDS.location,
        MISSION_ROLE_FORM_FIELDS.engagementTime,
        MISSION_ROLE_FORM_FIELDS.customerBudget,
        MISSION_ROLE_FORM_FIELDS.customerBudgetCurrency,
        MISSION_ROLE_FORM_FIELDS.customerBudgetFrequency,
      ];
    default:
      return [];
  }
};

export default MISSION_ROLE_FORM_FIELDS;

export { getLeftColFields, getRightColFields };
